import React from "react";
import { UEListData } from "@/app/interface/homepage";
import KeyFinancials from "@/app/alts/unlisted-shares/components/key-and-financials";
import StrengthsAndWeakness from "@/app/alts/unlisted-shares/components/strength-and-weekness";
import Shareholding from "@/app/alts/unlisted-shares/components/share-holding";
import BoardManagement from "@/app/alts/unlisted-shares/components/boared-management";
import StocksFaqs from "@/app/alts/unlisted-shares/components/stocks-faqs";
import { getProductSpecificFaqData } from "@/app/api/service/unlistedstocks";
import { useQuery } from "@tanstack/react-query";
import { formatIndianCurrency } from "@/app/utils/helper";

interface MetricProps {
    label: string;
    value: string | number;
}

const Metric: React.FC<MetricProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1 sm:gap-2">
        <span className="text-xs text-gray-500 sm:text-sm">{label}</span>
        <span className="text-base font-medium text-black sm:text-lg">{value}</span>
    </div>
);

interface FinancialMetricsProps {
    stockDetails: UEListData;
    isBasket?: boolean;
    ISINUnitPrice?: number;
}

const FundamentalsSection: React.FC<FinancialMetricsProps> = ({ stockDetails, ISINUnitPrice, isBasket }) => {
    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 sm:gap-x-16 sm:gap-y-8">
            <Metric
                label="Current Price"
                value={`${formatIndianCurrency(isBasket ? (ISINUnitPrice ?? 0) : stockDetails?.price?.price || 0)}`}
            />
            <Metric label="Market Cap" value={`${stockDetails?.distribution?.marketCap || 0}`} />
            <Metric
                label="ISIN"
                value={stockDetails?.displayISIN ? stockDetails?.displayISIN : stockDetails.ISIN || ""}
            />
            <Metric
                label="Face Value"
                value={`${formatIndianCurrency(stockDetails?.distribution?.faceValue || 0)}`}
            />
            {Number(stockDetails?.distribution?.priceToEarningsRatio) >= 0 && (
            <Metric
               label="P/E Ratio"
               value={stockDetails.distribution.priceToEarningsRatio}
            />
            )}
            <Metric label="EPS" value={stockDetails?.distribution?.earningsPerShare || 0} />
            <Metric label="P/B Ratio" value={stockDetails?.distribution?.priceToBookRatio || 0} />
            <Metric label="Book Value" value={stockDetails?.bookValue || 0} />
            <Metric label="Debt / Equity Ratio" value={stockDetails?.debtToEquityRatio || 0} />
        </div>
    );
};

const FinancialMetrics: React.FC<
    FinancialMetricsProps & { activeTab: string; setActiveTab: (tab: string) => void }
> = ({ stockDetails, activeTab, setActiveTab, isBasket, ISINUnitPrice }) => {
    const { data: productFaqData, isFetched: isProductFaqFetched } = useQuery({
        queryKey: ["productFaqListData"],
        queryFn: () => getProductSpecificFaqData(stockDetails?.productType, stockDetails?.product),
    });

    const hasFaqs = productFaqData?.data?.questions[0]?.content?.length > 0;

    const tabs = [
        "Fundamentals",
        "Key Financials",
        "Shareholding",
        "Strengths & Weaknesses",
        "Overview",
    ];
    if (isProductFaqFetched && hasFaqs) {
        tabs.push("FAQs");
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case "Fundamentals":
                return <FundamentalsSection stockDetails={stockDetails} ISINUnitPrice={ISINUnitPrice} isBasket={isBasket} />;
            case "Key Financials":
                return <KeyFinancials filteredData={stockDetails} />;
            case "Shareholding":
                return <Shareholding filteredData={stockDetails} />;
            case "Strengths & Weaknesses":
                return <StrengthsAndWeakness filteredData={stockDetails} />;
            case "Overview":
                return <BoardManagement filteredData={stockDetails} compact={true} />;
            case "FAQs":
                return hasFaqs ? (
                    <StocksFaqs
                        compact={true}
                        bgColor="bg-white"
                        productType={stockDetails?.productType}
                        product={stockDetails?.product}
                    />
                ) : null;
            default:
                return <FundamentalsSection stockDetails={stockDetails} ISINUnitPrice={ISINUnitPrice} isBasket={isBasket} />;
        }
    };

    return (
        <div
            className={`mt-12 pb-10 sm:mt-8 ${isBasket ? "max-w-max w-full" : "sm:w-[832px]"} sm:pt-12`}
        >
            <nav className="mb-6 overflow-x-auto border-b sm:mb-10">
                <ul className="flex min-w-max gap-6 whitespace-nowrap sm:gap-8">
                    {tabs.map((tab) => (
                        <li
                            key={tab}
                            className={`cursor-pointer pb-3 text-sm md:text-base ${
                                activeTab === tab
                                    ? "border-b-2 border-orange-500 text-orange-500"
                                    : "text-gray-500"
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === "Overview" ? "About Co." : tab}
                        </li>
                    ))}
                </ul>
            </nav>
            {activeTab === "Fundamentals" && (
                <h2 className="mb-6 text-xl font-semibold sm:mb-6 sm:text-3xl">Fundamentals</h2>
            )}
            {renderTabContent()}
        </div>
    );
};

export default FinancialMetrics;
